<script lang="ts">
    import * as t from '../../translations/Petitions.ts';
    
    export let title: string;
    export let imageUrl: string | null = null;
    export let dueDate: string | null = null;
    export let signatureCount: number = 0;
    export let spotlighted: boolean = false;
    
    // Format the due date if provided
    function formatDueDate(date: string | null): string | null {
        if (!date) return null;
        try {
            const d = new Date(date);
            return d.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            });
        } catch {
            return null;
        }
    }
    
    $: formattedDueDate = formatDueDate(dueDate);
</script>

<!-- Hero Banner Section -->
<div class="relative rounded-2xl overflow-hidden">
    {#if imageUrl}
        <!-- Banner with Image Background -->
        <div 
            class="relative aspect-[4/3] md:aspect-[16/9] bg-cover bg-center bg-no-repeat"
            style="background-image: url('{imageUrl}');"
        >
            <!-- Dark Overlay -->
            <div class="absolute inset-0 bg-black/50"></div>
            
            <!-- Content -->
            <div class="absolute inset-0 flex flex-col justify-between p-5">
                <!-- Featured Badge (top) -->
                {#if spotlighted}
                    <div>
                        <span class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gray-800/70 rounded-full backdrop-blur-sm">
                            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                            </svg>
                            {t.Featured_Label()}
                        </span>
                    </div>
                {:else}
                    <div></div>
                {/if}
                
                <!-- Title and Stats (bottom) -->
                <div>
                    <h1 class="!text-2xl md:!text-[1.625rem] !font-bold !text-white !leading-tight mb-3 break-words">
                        {title}
                    </h1>
                    
                    <!-- Stats Row -->
                    <div class="flex flex-wrap items-center gap-4 text-sm text-white/90">
                        {#if formattedDueDate}
                            <div class="flex items-center gap-1.5">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <span>{t.Due_By_Label()} {formattedDueDate}</span>
                            </div>
                        {/if}
                        
                        {#if signatureCount > 0}
                            <div class="flex items-center gap-1.5">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                </svg>
                                <span>{signatureCount.toLocaleString()}</span>
                            </div>
                        {/if}
                    </div>

                </div>
            </div>
        </div>
    {:else}
        <!-- Banner without Image (slate gradient - matches SpotlightGrid) -->
        <div class="relative aspect-[4/3] md:aspect-[16/9] bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
            <!-- Content -->
            <div class="absolute inset-0 flex flex-col justify-between p-5">
                <!-- Featured Badge (top) -->
                {#if spotlighted}
                    <div>
                        <span class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-white/20 rounded-full backdrop-blur-sm">
                            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                            </svg>
                            {t.Featured_Label()}
                        </span>
                    </div>
                {:else}
                    <div></div>
                {/if}
                
                <!-- Title and Stats (bottom) -->
                <div>
                    <h1 class="!text-2xl md:!text-[1.625rem] !font-bold !text-white !leading-tight mb-3 break-words">
                        {title}
                    </h1>
                    
                    <!-- Stats Row -->
                    <div class="flex flex-wrap items-center gap-4 text-sm text-white/90">
                        {#if formattedDueDate}
                            <div class="flex items-center gap-1.5">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <span>{t.Due_By_Label()} {formattedDueDate}</span>
                            </div>
                        {/if}
                        
                        {#if signatureCount > 0}
                            <div class="flex items-center gap-1.5">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                </svg>
                                <span>{signatureCount.toLocaleString()}</span>
                            </div>
                        {/if}
                    </div>

                </div>
            </div>
        </div>
    {/if}
</div>

